import {
  DialogContent,
  DialogClose,
  DialogTitle,
} from "@/components/ui/dialog";
import { Download, Copy } from "lucide-react";
import IconShield from '@/assets/icons/shield.svg';
import IconCross from '@/assets/icons/cross.svg';
import IconAlertTriangle from '@/assets/icons/alert_triangle.svg';
import IconXPlain from '@/assets/icons/x-plain.svg';
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/queryClient";
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { isMobileDevice } from "@/lib/mobileWalletUtils";
import { trackEvent } from '@/utils/analytics';
import { EventAction } from '@/types/analytics';
import { Partner } from '@/types/xdao';

interface SocialBadgeModalProps {
  socialBadgeUrl: string;
  onlyShowDownload?: boolean;
  partner?: Partner;
}

export default function SocialBadgeModal({ socialBadgeUrl, onlyShowDownload, partner }: SocialBadgeModalProps) {
  const { toast } = useToast();

  const handleModalClose = (reason: 'cross' | 'backdrop') => {
    trackEvent('ui_interaction', EventAction.MODAL_CLOSE, {
      label: `social_badge_modal_${reason}`,
      component: 'social_badge_modal',
      modal_type: onlyShowDownload ? 'view_only' : 'share_required'
    });
  };

  const handleSaveImage = async () => {
    try {
      // Fetch the image as a blob for reliable download (handles CORS better)
      const response = await api.get<Blob>(socialBadgeUrl, { responseType: 'blob' });
      const blob = response.data as Blob;

      // Create a temporary URL for the blob
      const url = URL.createObjectURL(blob);

      // Create and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = 'social-badge-nft.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL
      URL.revokeObjectURL(url);

      // Track successful save
      trackEvent('ui_interaction', EventAction.BUTTON_CLICK, {
        label: 'social_badge_save',
        component: 'social_badge_modal',
        modal_type: onlyShowDownload ? 'view_only' : 'share_required'
      });

      toast({
        title: "Success!",
        description: "Image downloaded successfully!",
      });
    } catch (error) {
      console.error('Failed to save image:', error);

      // Track failed save
      trackEvent('ui_interaction', EventAction.BUTTON_CLICK, {
        label: 'social_badge_save_failed',
        component: 'social_badge_modal',
        modal_type: onlyShowDownload ? 'view_only' : 'share_required',
        error_message: error instanceof Error ? error.message : 'Unknown error'
      });

      toast({
        title: "Error",
        description: "Failed to download image. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCopyImage = async () => {
    try {
      // Always convert to PNG for better clipboard compatibility
      await copyPngToClipboard(socialBadgeUrl);

      // Track successful copy
      trackEvent('ui_interaction', EventAction.BUTTON_CLICK, {
        label: 'social_badge_copy',
        component: 'social_badge_modal',
        modal_type: onlyShowDownload ? 'view_only' : 'share_required'
      });
    } catch (error) {
      console.error('Failed to copy image:', error);

      // Track failed copy
      trackEvent('ui_interaction', EventAction.BUTTON_CLICK, {
        label: 'social_badge_copy_failed',
        component: 'social_badge_modal',
        modal_type: onlyShowDownload ? 'view_only' : 'share_required',
        error_message: error instanceof Error ? error.message : 'Unknown error'
      });

      toast({
        title: "Error",
        description: "Failed to copy image. Please try again.",
        variant: "destructive",
      });
    }
  };

  const copyPngToClipboard = async (pngUrl: string) => {
    return new Promise<void>((resolve, reject) => {
      api.get<Blob>(pngUrl, { responseType: 'blob' })
        .then((response) => {
          const blob = response.data as Blob;
          if (!navigator.clipboard || !window.ClipboardItem) {
            throw new Error('Clipboard API not supported');
          }

          return navigator.clipboard.write([
            new ClipboardItem({
              'image/png': blob,
            }),
          ]);
        })
        .then(() => {
          toast({
            title: "Success!",
            description: "Image copied to clipboard!",
          });
          resolve();
        })
        .catch((error) => {
          console.warn('Direct copy failed, falling back to canvas method:', error);
        });
    });
  };

  const handleShareOnX = () => {
    // Track Share on X button click
    trackEvent('ui_interaction', EventAction.BUTTON_CLICK, {
      label: 'social_badge_share_x',
      component: 'social_badge_modal',
      modal_type: onlyShowDownload ? 'view_only' : 'share_required'
    });

    // Default tweet template
    const defaultTweetText = `Yap early, yap only, yap often.

@TweetMinting is connecting with the community and accumulating points in real-time.

Experience FREE tweet to mint NFT system, powered by @Tweetonium

Claim yours 👉`;

    // Use partner's template if available, otherwise use default
    const tweetText = partner?.socialBadgeTweetTemplate || defaultTweetText;

    // Encode the text for URL
    const encodedText = encodeURIComponent(tweetText);

    // Twitter Web Intent URL
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodedText}`;

    // Open Twitter compose window
    window.open(twitterUrl, '_blank', 'width=550,height=420');
  };

  return (
    <DialogContent
      className="w-[95%] max-w-2xl max-h-[90vh] mx-auto bg-[#171921] border border-gray-800 text-white px-4 py-4 overflow-hidden rounded-2xl"
      shouldShowCloseButton={false}
      onInteractOutside={() => handleModalClose('backdrop')}
    >
      {/* Dummy Title to fix the error of DialogContent not having a title */}
      <VisuallyHidden>
        <DialogTitle></DialogTitle>
      </VisuallyHidden>
      {/* Header */}
      <div className="flex flex-row items-center justify-between mb-4">
        <div className="flex items-center">
          <img
            src={IconShield}
            alt="shield"
            className="w-[1.38rem] h-[1.38rem] mr-2"
          />
          <h2 className="text-xl font-inter font-semibold text-white">
            My Social Badge (Personal NFT)
          </h2>
        </div>
        <DialogClose asChild>
          <button
            className="outline-none"
            onClick={() => handleModalClose('cross')}
          >
            <img
              src={IconCross}
              alt="cross"
              className="w-[1.88rem] h-[1.88rem]"
            />
          </button>
        </DialogClose>
      </div>

      {/* NFT Badge Image */}
      <div className="flex items-center justify-center mb-4 rounded-[0.63rem] py-4 bg-[#0E1016]">
        <img
          src={socialBadgeUrl}
          alt="Social Badge NFT"
          className="w-full max-w-sm max-h-[45vh] object-contain"
        />
      </div>

      {/* Info Text */}
      {!onlyShowDownload && (
        <div className="flex items-start bg-[#171921] border border-[#1F2937] rounded-lg p-3 mb-4">
          <img
            src={IconAlertTriangle}
            alt="alert triangle"
            className="w-5 h-5 mr-3"
          />
          <p className="font-inter text-tertiary text-sm">
            {partner?.socialBadgeInfoText || 'Required to share an X Post to become eligible for DAO access. Experience FREE tweet to mint NFT system, powered by Tweetonium'}
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className={`grid gap-4 ${onlyShowDownload ? 'grid-cols-1' : isMobileDevice() ? 'grid-cols-2' : 'grid-cols-3'}`}>
        <button
          onClick={handleSaveImage}
          className="flex items-center justify-center py-2 px-4 border-2 border-[#873ED0] rounded-xl text-white font-inter font-semibold text-base hover:bg-[#873ED0]/10 transition-colors"
        >
          <Download className="w-4 h-4 mr-[0.63rem]" />
          Save Image
        </button>

        {!onlyShowDownload && (
          <>
            {!isMobileDevice() && (
              <button
                onClick={handleCopyImage}
                className="flex items-center justify-center py-2 px-4 border-2 border-[#873ED0] rounded-xl text-white font-inter font-semibold text-base hover:bg-[#873ED0]/10 transition-colors"
              >
                <Copy className="w-4 h-4 mr-[0.63rem]" />
                Copy Image
              </button>
            )}
            <button
              onClick={handleShareOnX}
              className="flex items-center justify-center py-2 px-4 bg-[#873ED0] rounded-xl text-white font-inter font-semibold text-base hover:bg-[#9B4FE8] transition-colors"
            >
              Share on <img src={IconXPlain} alt="X" className="w-[1.38rem] h-[1.38rem] ml-[0.63rem]" />
            </button>
        </>
        )}
      </div>
    </DialogContent>
  );
}
